/**
 * Login Page
 * Phone + OTP authentication for riders
 * Updated: Jan 21, 2026 - FrogBasket branding
 */

import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '@/lib/auth';
import { parsePhoneNumber, formatPhoneNumber } from '@/lib/utils';
import { logger } from '@/lib/logger';

export default function LoginPage() {
  const navigate = useNavigate();
  const [phone, setPhone] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (!phone) {
      setError('Please enter your phone number');
      return;
    }

    // Parse and validate phone number
    const parsedPhone = parsePhoneNumber(phone);
    if (!parsedPhone.startsWith('+63') || parsedPhone.length !== 13) {
      setError('Please enter a valid Philippine phone number');
      return;
    }

    setIsLoading(true);

    try {
      await authService.requestOTP(parsedPhone);
      logger.info('OTP requested successfully', { phone: parsedPhone });
      
      // Navigate to OTP page
      navigate('/otp', { state: { phone: parsedPhone } });
    } catch (err: any) {
      logger.error('OTP request failed', err, { phone: parsedPhone });
      setError(err.response?.data?.message || 'Failed to send OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhoneChange = (value: string) => {
    setPhone(value);
    setError('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-500 via-green-600 to-teal-700 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            {/* FrogBasket Logo */}
            <div className="flex justify-center mb-4">
              <img 
                src="/frog_logo.png" 
                alt="FrogBasket" 
                className="w-20 h-20 object-contain"
                onError={(e) => {
                  // Fallback if logo not found
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            </div>
            <h1 className="text-3xl font-bold mb-2">
              <span className="text-emerald-600">Frog</span>
              <span className="text-amber-500">Basket</span>
            </h1>
            <p className="text-gray-600">Rider App</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number
              </label>
              <input
                id="phone"
                type="tel"
                placeholder="+63 9XX XXX XXXX"
                value={phone}
                onChange={(e) => handlePhoneChange(e.target.value)}
                disabled={isLoading}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
              {phone && (
                <p className="text-xs text-gray-500 mt-1">
                  {formatPhoneNumber(phone)}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading || !phone}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Sending...' : 'Request OTP'}
            </button>
          </form>

          {/* Footer */}
          <div className="mt-6 text-center text-sm text-gray-600">
            <p>Rider access only</p>
          </div>
        </div>
      </div>
    </div>
  );
}
